import { AxiosProgressEvent } from 'axios';
import HttpClient from '../../../services/http-client';
import { FileBase64 } from '../../../../model/file-base-64';
import { Pagination } from '../../../../model/pagination';
import { ISubmissionPagedRequest } from '../../../../model/ser/submission/request/submission-paged-request';
import { ISubmissionSubmitItem } from '../../../../model/ser/submission/request/submission-submit-request';
import { ISubmissionItemResponse } from '../../../../model/ser/submission/response/submission-item-response';
import { ISubmissionDetailResponse } from '../../../../model/ser/submission/response/submission-detail-response';
import { ISubmissionBatchResponse } from '../../../../model/ser/submission/response/submission-batch-response';
import { ISubmissionStatusResponse } from '../../../../model/ser/submission/response/submission-status-response';
import { ISubmissionVehicleResponse } from '../../../../model/ser/submission/response/submission-vehicle-response';
import { ISubmissionFileContentResponse } from '../../../../model/ser/submission/response/submission-file-content-response';
import { ISubmissionSubmitResponse } from '../../../../model/ser/submission/response/submission-submit-response';
import { ISubmissionCancelBatchResponse } from '../../../../model/ser/submission/response/submission-cancel-batch-response';
import { buildBatchFormData, buildSubmissionFormData } from '../../../../presentation/ser/submission-send/submission-form-data';

const defaultPath = 'Submission';

// Percentual (0-100) do upload do multipart
export type UploadProgressHandler = (percent: number) => void;

function toProgress(onProgress?: UploadProgressHandler) {
    return onProgress
        ? (event: AxiosProgressEvent) => onProgress(event.total ? Math.round((event.loaded * 100) / event.total) : 0)
        : undefined;
}

// Paginação 0-based; arrays saem como StatusId=1&StatusId=2 (paramsSerializer do http-client)
const list = async (params: ISubmissionPagedRequest): Promise<Pagination<ISubmissionItemResponse>> => {
    return await HttpClient.executeRequest({
        method: 'get',
        url: `/${defaultPath}`,
        params: {
            Page: params.page,
            PageSize: params.pageSize,
            StartDate: params.startDate,
            EndDate: params.endDate,
            PublishStartDate: params.publishStartDate,
            PublishEndDate: params.publishEndDate,
            StatusId: params.statusIds?.length ? params.statusIds : undefined,
            Types: params.types?.length ? params.types : undefined,
            VehicleId: params.vehicleId,
            BatchId: params.batchId,
            ClientId: params.clientId,
            Title: params.title || undefined,
            Content: params.content || undefined,
        }
    }).then((response) => Object.assign(new Pagination<ISubmissionItemResponse>(), response.data));
};

const getById = async (id: number): Promise<ISubmissionDetailResponse> => {
    return await HttpClient.executeRequest({ method: 'get', url: `/${defaultPath}/${id}` })
        .then((response) => response.data as ISubmissionDetailResponse);
};

const getBatch = async (batchId: number): Promise<ISubmissionBatchResponse> => {
    return await HttpClient.executeRequest({ method: 'get', url: `/${defaultPath}/Batch/${batchId}` })
        .then((response) => response.data as ISubmissionBatchResponse);
};

const listStatus = async (): Promise<ISubmissionStatusResponse[]> => {
    return await HttpClient.executeRequest({ method: 'get', url: `/${defaultPath}/Status` })
        .then((response) => response.data || []);
};

const listVehicles = async (clientId?: number): Promise<ISubmissionVehicleResponse[]> => {
    return await HttpClient.executeRequest({ method: 'get', url: `/${defaultPath}/Vehicles`, params: { ClientId: clientId } })
        .then((response) => response.data || []);
};

const submit = async (item: ISubmissionSubmitItem, clientId?: number, onProgress?: UploadProgressHandler): Promise<ISubmissionSubmitResponse> => {
    return await HttpClient.executeRequest({
        method: 'post',
        url: `/${defaultPath}`,
        data: buildSubmissionFormData(item, clientId),
        onUploadProgress: toProgress(onProgress),
    }).then((response) => response.data || {});
};

const submitBatch = async (items: ISubmissionSubmitItem[], clientId?: number, onProgress?: UploadProgressHandler): Promise<ISubmissionSubmitResponse> => {
    return await HttpClient.executeRequest({
        method: 'post',
        url: `/${defaultPath}/Batch`,
        data: buildBatchFormData(items, clientId),
        onUploadProgress: toProgress(onProgress),
    }).then((response) => response.data || {});
};

const cancel = async (id: number, reason: string): Promise<void> => {
    await HttpClient.executeRequest({ method: 'post', url: `/${defaultPath}/${id}/Cancel`, data: { reason } });
};

const cancelBatch = async (batchId: number, reason: string): Promise<ISubmissionCancelBatchResponse> => {
    return await HttpClient.executeRequest({ method: 'post', url: `/${defaultPath}/Batch/${batchId}/Cancel`, data: { reason } })
        .then((response) => response.data || {});
};

function toFile(data: any): FileBase64 {
    return new FileBase64(data?.originalName || data?.name, data?.contentType, data?.source);
}

const downloadAll = async (id: number): Promise<FileBase64> => {
    return await HttpClient.executeRequest({ method: 'get', url: `/${defaultPath}/${id}/Files/Download` })
        .then((response) => toFile(response.data));
};

const downloadFile = async (id: number, fileId: number): Promise<FileBase64> => {
    return await HttpClient.executeRequest({ method: 'get', url: `/${defaultPath}/${id}/Files/${fileId}/Download` })
        .then((response) => toFile(response.data));
};

const getFileContent = async (id: number, fileId: number): Promise<ISubmissionFileContentResponse> => {
    return await HttpClient.executeRequest({ method: 'get', url: `/${defaultPath}/${id}/Files/${fileId}/Content` })
        .then((response) => response.data as ISubmissionFileContentResponse);
};

const reprocessContent = async (id: number, fileId: number): Promise<void> => {
    await HttpClient.executeRequest({ method: 'post', url: `/${defaultPath}/${id}/Files/${fileId}/Content/Reprocess` });
};

const SubmissionAppService = {
    list, getById, getBatch, listStatus, listVehicles, submit, submitBatch,
    cancel, cancelBatch, downloadAll, downloadFile, getFileContent, reprocessContent,
};

export default SubmissionAppService;
